import React, { useEffect, useRef, useState } from 'react';
import { Box, IconButton, Slider, Typography } from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import ReplayIcon from '@mui/icons-material/Replay';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import CloseIcon from '@mui/icons-material/Close';
import Hls from 'hls.js';
import { FONT_ENUM } from '../../../types/global/fonts.types';
import { BRAND_LUXE_PRIMARY } from '../brandDashboardTokens';
import { formatTime } from '../../../utils/AppUtils';
import { api } from '../../../configurations/api';
import { cookieUrl } from '../../../configurations/api/url';
import { httpRequest } from '../../../redux/types';

const CONTROLS_HEIGHT = 72;

const isHlsUrl = (url: string) => /\.m3u8(\?|#|$)/i.test(url);

async function ensureProjectPlaybackCookie(videoId: string): Promise<void> {
    const id = String(videoId || '').trim();
    if (!id) return;
    await api(`${cookieUrl}/${id}`, httpRequest.GET, null, 2, 2000);
}

interface VideoModalPlayerProps {
    src: string;
    /** CloudFront cookie bootstrap for HLS segment trees (project.video_id). */
    videoId?: string | null;
    /** When provided, shows a close button at top-right */
    onClose?: () => void;
    /** When provided, renders a diagonal watermark over the video area */
    watermarkText?: string;
    /** Max height of the video element. Defaults to '70vh' */
    maxVideoHeight?: string | number;
}

export function VideoModalPlayer({
    src,
    videoId,
    onClose,
    watermarkText,
    maxVideoHeight = '70vh',
}: VideoModalPlayerProps) {
    const videoRef = useRef<HTMLVideoElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const hlsRef = useRef<Hls | null>(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [isMuted, setIsMuted] = useState(false);
    const [volume, setVolume] = useState(1);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [hasEnded, setHasEnded] = useState(false);
    const [isFullscreen, setIsFullscreen] = useState(false);

    useEffect(() => {
        const handleFsChange = () => setIsFullscreen(!!document.fullscreenElement);
        document.addEventListener('fullscreenchange', handleFsChange);
        return () => document.removeEventListener('fullscreenchange', handleFsChange);
    }, []);

    useEffect(() => {
        const video = videoRef.current;
        if (!video || !src) return;

        let cancelled = false;
        setIsPlaying(false);
        setHasEnded(false);
        setCurrentTime(0);
        setDuration(0);

        const tearDownHls = () => {
            if (hlsRef.current) {
                hlsRef.current.destroy();
                hlsRef.current = null;
            }
        };

        const start = async () => {
            tearDownHls();
            video.removeAttribute('src');
            video.load();

            if (!isHlsUrl(src)) {
                video.src = src;
                return;
            }

            if (videoId) {
                try {
                    await ensureProjectPlaybackCookie(String(videoId));
                } catch {
                    /* still try HLS */
                }
            }
            if (cancelled) return;

            if (Hls.isSupported()) {
                const hls = new Hls({
                    enableWorker: true,
                    xhrSetup: xhr => {
                        xhr.withCredentials = true;
                    },
                });
                hlsRef.current = hls;
                hls.loadSource(src);
                hls.attachMedia(video);
            } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
                video.src = src;
            }
        };

        void start();

        const cookieRefresh =
            videoId &&
            isHlsUrl(src) &&
            window.setInterval(() => {
                void ensureProjectPlaybackCookie(String(videoId)).catch(() => undefined);
            }, 45 * 60 * 1000);

        return () => {
            cancelled = true;
            if (cookieRefresh) window.clearInterval(cookieRefresh);
            tearDownHls();
        };
    }, [src, videoId]);

    const togglePlay = () => {
        const v = videoRef.current;
        if (!v) return;
        if (hasEnded) {
            v.currentTime = 0;
            setHasEnded(false);
        }
        if (v.paused) {
            void v.play();
            setIsPlaying(true);
        } else {
            v.pause();
            setIsPlaying(false);
        }
    };

    const toggleMute = () => {
        const v = videoRef.current;
        if (!v) return;
        const next = !isMuted;
        v.muted = next;
        setIsMuted(next);
    };

    const handleVolumeChange = (_: Event, val: number | number[]) => {
        const v = videoRef.current;
        if (!v) return;
        const vol = val as number;
        v.volume = vol;
        setVolume(vol);
        if (vol === 0) {
            v.muted = true;
            setIsMuted(true);
        } else if (isMuted) {
            v.muted = false;
            setIsMuted(false);
        }
    };

    const handleSeek = (_: Event, val: number | number[]) => {
        const v = videoRef.current;
        if (!v || !duration) return;
        const t = val as number;
        v.currentTime = t;
        setCurrentTime(t);
    };

    const handleFullscreen = () => {
        if (document.fullscreenElement) {
            document.exitFullscreen();
        } else {
            containerRef.current?.requestFullscreen?.();
        }
    };

    return (
        <Box
            ref={containerRef}
            sx={{
                position: 'relative',
                bgcolor: '#000',
                userSelect: 'none',
                ...(isFullscreen && {
                    display: 'flex',
                    flexDirection: 'column',
                    width: '100vw',
                    height: '100vh',
                }),
            }}>
            {onClose && (
                <IconButton
                    onClick={onClose}
                    sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        zIndex: 30,
                        bgcolor: 'rgba(0,0,0,0.65)',
                        color: '#fff',
                        '&:hover': { bgcolor: 'rgba(0,0,0,0.9)' },
                    }}>
                    <CloseIcon sx={{ fontSize: 20 }} />
                </IconButton>
            )}

            {watermarkText && (
                <Box
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: CONTROLS_HEIGHT,
                        pointerEvents: 'none',
                        overflow: 'hidden',
                        zIndex: 2,
                    }}>
                    {Array.from({ length: 6 }).map((_, row) =>
                        Array.from({ length: 4 }).map((__, col) => (
                            <Typography
                                key={`${row}-${col}`}
                                sx={{
                                    position: 'absolute',
                                    top: `${row * 22 - 5}%`,
                                    left: `${col * 30 - 5}%`,
                                    transform: 'rotate(-35deg)',
                                    transformOrigin: 'center',
                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                    fontSize: 13,
                                    color: 'rgba(255,255,255,0.18)',
                                    whiteSpace: 'nowrap',
                                    userSelect: 'none',
                                    letterSpacing: '0.04em',
                                }}>
                                {watermarkText}
                            </Typography>
                        )),
                    )}
                </Box>
            )}

            <Box
                onClick={togglePlay}
                sx={{
                    lineHeight: 0,
                    cursor: 'pointer',
                    ...(isFullscreen && {
                        flex: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        overflow: 'hidden',
                    }),
                }}>
                <video
                    ref={videoRef}
                    playsInline
                    onContextMenu={e => e.preventDefault()}
                    onTimeUpdate={() => setCurrentTime(videoRef.current?.currentTime ?? 0)}
                    onLoadedMetadata={() => setDuration(videoRef.current?.duration ?? 0)}
                    onEnded={() => {
                        setIsPlaying(false);
                        setHasEnded(true);
                    }}
                    style={{
                        width: '100%',
                        maxHeight: isFullscreen ? '100%' : (maxVideoHeight as string),
                        height: isFullscreen ? '100%' : undefined,
                        objectFit: isFullscreen ? 'contain' : undefined,
                        display: 'block',
                        outline: 'none',
                    }}
                />
            </Box>

            <Box
                sx={{
                    bgcolor: 'rgba(0,0,0,0.88)',
                    px: 1.5,
                    pt: 0.5,
                    pb: 1.5,
                    position: 'relative',
                    zIndex: 10,
                    flexShrink: 0,
                }}>
                <Slider
                    size="small"
                    value={currentTime}
                    min={0}
                    max={duration || 100}
                    step={0.1}
                    onChange={handleSeek}
                    sx={{
                        color: BRAND_LUXE_PRIMARY,
                        height: 3,
                        py: '8px',
                        '& .MuiSlider-thumb': {
                            width: 12,
                            height: 12,
                            '&:hover, &.Mui-focusVisible': { boxShadow: '0 0 0 6px rgba(255,201,101,0.2)' },
                        },
                        '& .MuiSlider-rail': { bgcolor: 'rgba(255,255,255,0.25)' },
                    }}
                />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <IconButton onClick={togglePlay} size="small" sx={{ color: '#fff' }}>
                        {hasEnded ? (
                            <ReplayIcon sx={{ fontSize: 22 }} />
                        ) : isPlaying ? (
                            <PauseIcon sx={{ fontSize: 22 }} />
                        ) : (
                            <PlayArrowIcon sx={{ fontSize: 22 }} />
                        )}
                    </IconButton>

                    <Typography
                        sx={{
                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                            fontSize: 12,
                            color: 'rgba(255,255,255,0.7)',
                            minWidth: 90,
                        }}>
                        {formatTime(currentTime)} / {formatTime(duration)}
                    </Typography>

                    <Box sx={{ flex: 1 }} />

                    <IconButton onClick={toggleMute} size="small" sx={{ color: '#fff' }}>
                        {isMuted || volume === 0 ? (
                            <VolumeOffIcon sx={{ fontSize: 20 }} />
                        ) : (
                            <VolumeUpIcon sx={{ fontSize: 20 }} />
                        )}
                    </IconButton>

                    <Slider
                        size="small"
                        value={isMuted ? 0 : volume}
                        min={0}
                        max={1}
                        step={0.05}
                        onChange={handleVolumeChange}
                        sx={{
                            width: 72,
                            color: '#fff',
                            height: 3,
                            py: '8px',
                            '& .MuiSlider-thumb': { width: 10, height: 10 },
                            '& .MuiSlider-rail': { bgcolor: 'rgba(255,255,255,0.25)' },
                        }}
                    />

                    <IconButton onClick={handleFullscreen} size="small" sx={{ color: '#fff' }}>
                        {isFullscreen ? (
                            <FullscreenExitIcon sx={{ fontSize: 22 }} />
                        ) : (
                            <FullscreenIcon sx={{ fontSize: 22 }} />
                        )}
                    </IconButton>
                </Box>
            </Box>
        </Box>
    );
}
